import { array, bool, func, oneOf, shape, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, createSearchParams, useNavigate } from 'react-router-dom';

import { ApiImg, Banner, Modal, SanitizedHtml, StrapiImg } from 'components';
import { PAGE_TYPES } from 'constants';
import { formatLocalizedCurrency } from 'helpers';
import { routes } from 'router';
import { setShowCart } from 'store';

const AddCartSuccessModal = ({ confirmationModal, setConfirmationModal, pageType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onRedirectToSearch = () => {
    navigateToSearch();
    dispatch(setShowCart(true));
    handleClose();
  };

  const handleClose = () => setConfirmationModal({ open: false, content: null, alert: null });

  const navigateToSearch = () => {
    navigate({
      pathname: routes.results.path,
      search: createSearchParams({ type: pageType }).toString()
    });
  };

  return (
    <Modal
      size="x-small"
      isOpen={confirmationModal.open}
      body={
        <>
          <h2 className="h2 underlined-yellow mb-12 text-base md:text-lg">
            {t('cart:addSuccess', { count: confirmationModal?.content?.length })}
          </h2>

          {confirmationModal?.alert && (
            <Banner type="warning" className="mb-5">
              <SanitizedHtml content={confirmationModal.alert} />
            </Banner>
          )}

          <ul className="flex flex-col gap-y-2 md:gap-y-8" data-test-id="success-cart-product">
            {confirmationModal.content?.map((product, index) => (
              <li key={`product-cart-${index}`} className="flex items-center gap-y-2 gap-x-12">
                {product?.typeItem === PAGE_TYPES.giftcards ? (
                  <div className="w- h-28 rounded-lg shrink-0 overflow-hidden hidden md:block">
                    <StrapiImg image={product.vignette} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-28 h-28 rounded-lg shrink-0 overflow-hidden hidden md:block">
                    <ApiImg src={product.vignette} width="112" height="112" />
                  </div>
                )}

                <div className="w-full flex flex-col justify-between gap-y-2 md:gap-y-8">
                  <div className="flex-center-between">
                    <span className="font-semibold uppercase">{product.label}</span>
                    {product?.subTypeLogo && (
                      <div className="w-14">
                        <img
                          src={product.subTypeLogo}
                          className="w-full h-full object-cover"
                          alt=""
                          width="56"
                          height="20"
                        />
                      </div>
                    )}
                  </div>

                  {product?.subLabel && <div className="text-gray-600">{product.subLabel}</div>}

                  <div className="flex-center-between">
                    <span>{formatLocalizedCurrency(product.total)}</span>
                    <span>
                      {t('form:quantity')} : {product.quantity}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex-center-between flex-wrap gap-4 mt-12">
            <Link to={`${routes.results.path}?type=${pageType}`} className="btn outlined w-full md:w-fit">
              {t('cart:keepShopping')}
            </Link>

            <button type="button" className="btn w-full md:w-fit" onClick={onRedirectToSearch}>
              {t('cart:seeCart')}
            </button>
          </div>
        </>
      }
      onClose={handleClose}
    />
  );
};

AddCartSuccessModal.propTypes = {
  confirmationModal: shape({
    open: bool,
    content: array,
    alert: string
  }),
  setConfirmationModal: func,
  pageType: oneOf([PAGE_TYPES.leisures, PAGE_TYPES.giftcards])
};

export default AddCartSuccessModal;
